import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Truck, DollarSign, Wrench, UserCog, Package, FileBarChart,
  Settings, ChevronLeft, ChevronRight, LogOut, Shield, Building2, FileText, Landmark,
  ListChecks, Tags, Database, ChevronDown, ClipboardList, Fuel, Circle, CalendarClock,
  History, TrendingUp, AlertTriangle, ArrowDownRight, ArrowUpRight, PiggyBank,
} from 'lucide-react';
import { BRAND } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';

const MAINT_ITEMS = [
  { to: '/manutencao', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/manutencao/os', label: 'Ordens de Serviço', icon: Wrench },
  { to: '/manutencao/preventivas', label: 'Manutenção Preventiva', icon: ClipboardList },
  { to: '/manutencao/corretivas', label: 'Manutenção Corretiva', icon: AlertTriangle },
  { to: '/manutencao/agenda', label: 'Agenda', icon: CalendarClock },
  { to: '/manutencao/checklists', label: 'Checklists', icon: ClipboardList },
  { to: '/manutencao/pneus', label: 'Pneus', icon: Circle },
  { to: '/manutencao/abastecimentos', label: 'Abastecimentos', icon: Fuel },
  { to: '/manutencao/historico-tecnico', label: 'Histórico Técnico', icon: History },
  { to: '/manutencao/indicadores', label: 'Indicadores', icon: TrendingUp },
  { to: '/manutencao/relatorios', label: 'Relatórios', icon: FileBarChart },
  { to: '/manutencao/configuracoes', label: 'Configurações', icon: Settings },
];

const FINANCE_ITEMS = [
  { to: '/financeiro', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/financeiro/lancamentos', label: 'Lançamentos', icon: DollarSign },
  { to: '/financeiro/pagar', label: 'Contas a Pagar', icon: ArrowDownRight },
  { to: '/financeiro/receber', label: 'Contas a Receber', icon: ArrowUpRight },
  { to: '/financeiro/fluxo-caixa', label: 'Fluxo de Caixa', icon: TrendingUp },
  { to: '/financeiro/contratos-rentabilidade', label: 'Rentabilidade Contratos', icon: FileText },
  { to: '/financeiro/custos-frota', label: 'Custos da Frota', icon: Truck },
  { to: '/financeiro/bancos', label: 'Bancos e Contas', icon: Landmark },
  { to: '/financeiro/orcamentos', label: 'Orçamentos', icon: PiggyBank },
  { to: '/financeiro/relatorios', label: 'Relatórios & DRE', icon: FileBarChart },
];

const RH_ITEMS = [
  { to: '/rh', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/rh/funcionarios', label: 'Funcionários', icon: Users },
];

const menu = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/clientes', label: 'Clientes', icon: Users },
  { to: '/contratos', label: 'Contratos', icon: FileText },
  { to: '/frota', label: 'Frota', icon: Truck },
  { group: 'manutencao', label: 'Manutenção', icon: Wrench, children: MAINT_ITEMS },
  { to: '/estoque', label: 'Estoque e Compras', icon: Package },
  { group: 'financeiro', label: 'Financeiro', icon: DollarSign, children: FINANCE_ITEMS },
  { group: 'rh', label: 'RH', icon: UserCog, children: RH_ITEMS },
  { to: '/operacao', label: 'Operação', icon: FileBarChart },
  { to: '/bi', label: 'Business Intelligence', icon: FileBarChart },
];

const portalsMenu = [
  { to: '/portal-cliente', label: 'Portal do Cliente', icon: Building2 },
  { to: '/portal-motorista', label: 'Portal do Motorista', icon: UserCog },
];

const adminMenu = [
  { to: '/usuarios', label: 'Usuários', icon: Users },
  { to: '/perfis', label: 'Perfis', icon: Shield },
  { to: '/empresa', label: 'Empresa', icon: Building2 },
  { to: '/cadastros', label: 'Cadastros Mestres', icon: Database },
  { to: '/centros-custo', label: 'Centros de Custo', icon: Landmark },
  { to: '/categorias', label: 'Categorias', icon: ListChecks },
  { to: '/tags', label: 'Tags', icon: Tags },
  { to: '/admin', label: 'Administração', icon: Settings },
  { to: '/configuracoes', label: 'Configurações', icon: Settings },
];

const linkClass = ({ isActive }) =>
  `group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
    isActive
      ? 'bg-[#C52429] text-white shadow-sm'
      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
  }`;

const subLinkClass = ({ isActive }) =>
  `flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
    isActive
      ? 'bg-[#C52429]/10 text-brand font-medium'
      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
  }`;

function GroupItem({ group, collapsed }) {
  const location = useLocation();
  const isInside = group.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'));
  const [open, setOpen] = useState(isInside);
  React.useEffect(() => { if (isInside) setOpen(true); }, [isInside]);

  if (collapsed) {
    // In collapsed mode, show the group icon that links to the first child
    return (
      <NavLink
        to={group.children[0].to}
        className={({ isActive }) => `${linkClass({ isActive: isActive || isInside })}`}
        title={group.label}
      >
        <group.icon className="h-5 w-5 shrink-0" />
      </NavLink>
    );
  }

  return (
    <div>
      <button
        type="button"
        data-testid={`sidebar-group-${group.group}`}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isInside
            ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 font-medium'
            : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
        }`}
      >
        <group.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="mt-1 ml-4 pl-2 border-l border-neutral-200 dark:border-neutral-800 space-y-0.5">
          {group.children.map((c) => (
            <li key={c.to}>
              <NavLink
                to={c.to}
                end={c.end}
                className={subLinkClass}
                data-testid={`sidebar-sub-${c.to.replace(/\//g, '-')}`}
              >
                <c.icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{c.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'Administrador';

  return (
    <aside
      className={`sidebar-scroll fixed left-0 top-0 z-40 h-screen border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 transition-all duration-200 flex flex-col ${
        collapsed ? 'w-[72px]' : 'w-64'
      }`}
    >
      <div className="h-16 flex items-center gap-2 px-4 border-b border-neutral-200 dark:border-neutral-800">
        <img src={BRAND.logoUrl} alt={BRAND.name} className="h-9 w-9 rounded-full object-contain bg-white" />
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-[15px] text-brand tracking-tight">DANY TRANS</span>
            <span className="text-[10px] text-neutral-500 dark:text-neutral-400 uppercase">Gestão de Frota</span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto sidebar-scroll py-3 px-2">
        {!collapsed && <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-500">Principal</p>}
        <ul className="space-y-0.5">
          {menu.map((m) => (
            <li key={m.to || m.group}>
              {m.children ? (
                <GroupItem group={m} collapsed={collapsed} />
              ) : (
                <NavLink to={m.to} className={linkClass} title={collapsed ? m.label : undefined}>
                  <m.icon className="h-5 w-5 shrink-0" />
                  {!collapsed && <span className="truncate">{m.label}</span>}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        {isAdmin && (
          <>
            {!collapsed && <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-500">Administração</p>}
            <ul className="space-y-0.5 mt-1">
              {adminMenu.map((m) => (
                <li key={m.to}>
                  <NavLink to={m.to} className={linkClass} title={collapsed ? m.label : undefined}>
                    <m.icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span className="truncate">{m.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
        {!collapsed && <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-500">Portais</p>}
        <ul className="space-y-0.5 mt-1">
          {portalsMenu.map((m) => (
            <li key={m.to}>
              <NavLink to={m.to} className={linkClass} title={collapsed ? m.label : undefined}>
                <m.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="truncate">{m.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="border-t border-neutral-200 dark:border-neutral-800 p-2 space-y-1">
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          title={collapsed ? 'Sair' : undefined}
          data-testid="sidebar-logout"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed && <span>Sair</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : (<><ChevronLeft className="h-4 w-4" /><span>Recolher</span></>)}
        </button>
      </div>
    </aside>
  );
}
